import {
	Box,
	Button,
	Card,
	Divider,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import EmoInfoForm from './components/EmoInfoForm';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import {
	useDeleteEmoMutation,
	useGetEmoDetailQuery,
} from '@/api/information/emoApiSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AppImageView from '@/common/components/form-view/AppImageView';
import { showLabel } from '@/utils/helper';

export default function EmoDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteEmo] = useDeleteEmoMutation();
	const { emoId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');

	const typeList = [
		{ id: 0, name: t('emo.web') },
		{ id: 1, name: t('emo.pdf') },
	];

	const { data: details } = useGetEmoDetailQuery(emoId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('emo.deleteItem')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('emo.isDeleteItem')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteEmo(emoId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteItemSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate('/information-manage/emo-support-list', { replace: true });
					}
				}}
			/>
		));
	};
	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.itemDetail')}
					</Typography>
					<Button
						endIcon={<DeleteOutlined />}
						onClick={() => handleDelete()}
						sx={{ marginRight: '24px' }}
						variant='contained'
					>
						{t('button.delete')}
					</Button>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('emo.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('emo.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('emo.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>{t('emo.content')}</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('emo.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('emo.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={
														currentTab == 'zh' ? t('emo.title') : t('emo.title')
													}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={
														currentTab == 'zh'
															? t('emo.description')
															: t('emo.description')
													}
													value={
														currentTab == 'zh'
															? details?.data.descriptionZh
															: currentTab == 'en'
															? details?.data.descriptionEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('emo.coverImage')}
													imageElement={
														details?.data.coverImage && (
															<img
																src={details?.data.coverImage.url}
																width={110}
																height={132}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('emo.type')}
													value={
														showLabel(
															String(details?.data.mediaType),
															typeList
														) || '-'
													}
												/>
											</Grid>
											{details?.data.mediaType == 0 && (
												<Grid item xs={12}>
													<AppFieldView
														label={t('emo.webLink')}
														value={details?.data.link || '-'}
														link={details?.data.link || '-'}
													/>
												</Grid>
											)}
											{details?.data.mediaType == 1 && (
												<Grid item xs={12}>
													<AppFieldView
														label={t('emo.pdfFile')}
														value={details?.data?.link || '-'}
														link={details?.data?.pdf?.url}
													/>
												</Grid>
											)}
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>{t('emo.content')}</Typography>
										</Box>
										<EmoInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
